import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { cn } from '../../utils/cn';

interface OrderBookLevel {
  price: number;
  amount: number;
}

interface DepthChartProps {
  bids: OrderBookLevel[];
  asks: OrderBookLevel[];
  lastPrice: number;
  pair: string;
  height?: number;
}

const COLORS = {
  bg: '#080b0f',
  grid: 'rgba(255, 255, 255, 0.03)',
  text: '#8b949e',
  textStrong: '#f0f6fc',
  bidFill: 'rgba(0, 212, 170, 0.15)',
  bidStroke: '#00D4AA',
  askFill: 'rgba(255, 107, 107, 0.15)',
  askStroke: '#FF6B6B',
  midLine: '#3B82F6',
};

export default function DepthChart({ bids, asks, lastPrice, pair, height = 300 }: DepthChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredData, setHoveredData] = useState<{
    price: number;
    total: number;
    side: 'bid' | 'ask';
    x: number;
    y: number;
  } | null>(null);

  // Process order book into cumulative depth
  const processedData = useMemo(() => {
    // Sort and calculate cumulative totals
    const sortedBids = [...bids].sort((a, b) => b.price - a.price);
    const sortedAsks = [...asks].sort((a, b) => a.price - b.price);

    let cumulativeBids: { price: number; total: number }[] = [];
    let cumulativeAsks: { price: number; total: number }[] = [];

    let bidTotal = 0;
    for (const bid of sortedBids) {
      bidTotal += bid.amount;
      cumulativeBids.push({ price: bid.price, total: bidTotal });
    }

    let askTotal = 0;
    for (const ask of sortedAsks) {
      askTotal += ask.amount;
      cumulativeAsks.push({ price: ask.price, total: askTotal });
    }

    // Find price range
    const allPrices = [...cumulativeBids.map(b => b.price), ...cumulativeAsks.map(a => a.price)];
    const minPrice = Math.min(...allPrices) * 0.995;
    const maxPrice = Math.max(...allPrices) * 1.005;

    // Find max total for Y scaling
    const maxTotal = Math.max(
      cumulativeBids.length > 0 ? cumulativeBids[cumulativeBids.length - 1].total : 0,
      cumulativeAsks.length > 0 ? cumulativeAsks[cumulativeAsks.length - 1].total : 0
    );

    return {
      bids: cumulativeBids,
      asks: cumulativeAsks,
      minPrice,
      maxPrice,
      priceRange: maxPrice - minPrice,
      maxTotal,
    };
  }, [bids, asks]);

  // Format helpers
  const formatPrice = useCallback((price: number) => {
    if (price >= 10000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 100) return price.toFixed(2);
    if (price >= 1) return price.toFixed(4);
    return price.toFixed(6);
  }, []);

  const formatVolume = useCallback((vol: number) => {
    if (vol >= 1e9) return (vol / 1e9).toFixed(2) + 'B';
    if (vol >= 1e6) return (vol / 1e6).toFixed(2) + 'M';
    if (vol >= 1e3) return (vol / 1e3).toFixed(2) + 'K';
    return vol.toFixed(2);
  }, []);

  // Render chart
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const chartHeight = height;
    const padding = { top: 20, right: 70, bottom: 30, left: 10 };
    const chartWidth = width - padding.left - padding.right;
    const drawHeight = chartHeight - padding.top - padding.bottom;

    // Clear canvas
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, width, chartHeight);

    const { bids, asks, minPrice, maxPrice, priceRange, maxTotal } = processedData;

    if (bids.length === 0 && asks.length === 0) {
      ctx.fillStyle = COLORS.text;
      ctx.font = '14px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No order book data', width / 2, chartHeight / 2);
      return;
    }

    // Coordinate helpers
    const priceToX = (price: number) => padding.left + ((price - minPrice) / priceRange) * chartWidth;
    const totalToY = (total: number) => padding.top + (1 - total / maxTotal) * drawHeight;

    // Draw grid
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (i / 5) * drawHeight;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }

    // Vertical grid lines
    for (let i = 0; i <= 10; i++) {
      const x = padding.left + (i / 10) * chartWidth;
      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, chartHeight - padding.bottom);
      ctx.stroke();
    }

    // Draw bids (step area chart)
    if (bids.length > 0) {
      ctx.beginPath();
      ctx.moveTo(priceToX(minPrice), chartHeight - padding.bottom);

      // Start from highest bid price
      const reversedBids = [...bids].reverse();
      for (let i = 0; i < reversedBids.length; i++) {
        const { price, total } = reversedBids[i];
        const x = priceToX(price);
        const y = totalToY(total);

        if (i === 0) {
          ctx.lineTo(x, chartHeight - padding.bottom);
        }
        ctx.lineTo(x, y);

        // Step to next price level
        if (i < reversedBids.length - 1) {
          const nextPrice = reversedBids[i + 1].price;
          ctx.lineTo(priceToX(nextPrice), y);
        }
      }

      // Close path
      ctx.lineTo(priceToX(reversedBids[reversedBids.length - 1].price), chartHeight - padding.bottom);
      ctx.closePath();

      // Fill
      const bidGradient = ctx.createLinearGradient(0, padding.top, 0, chartHeight - padding.bottom);
      bidGradient.addColorStop(0, COLORS.bidFill);
      bidGradient.addColorStop(1, 'rgba(0, 212, 170, 0.02)');
      ctx.fillStyle = bidGradient;
      ctx.fill();

      // Stroke
      ctx.strokeStyle = COLORS.bidStroke;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Draw asks (step area chart)
    if (asks.length > 0) {
      ctx.beginPath();

      for (let i = 0; i < asks.length; i++) {
        const { price, total } = asks[i];
        const x = priceToX(price);
        const y = totalToY(total);

        if (i === 0) {
          ctx.moveTo(x, chartHeight - padding.bottom);
          ctx.lineTo(x, y);
        } else {
          const prevPrice = asks[i - 1].price;
          ctx.lineTo(priceToX(prevPrice), y);
          ctx.lineTo(x, y);
        }
      }

      // Close path
      const lastAsk = asks[asks.length - 1];
      ctx.lineTo(priceToX(lastAsk.price), chartHeight - padding.bottom);
      ctx.closePath();

      // Fill
      const askGradient = ctx.createLinearGradient(0, padding.top, 0, chartHeight - padding.bottom);
      askGradient.addColorStop(0, COLORS.askFill);
      askGradient.addColorStop(1, 'rgba(255, 107, 107, 0.02)');
      ctx.fillStyle = askGradient;
      ctx.fill();

      // Stroke
      ctx.strokeStyle = COLORS.askStroke;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Draw mid price line
    if (lastPrice >= minPrice && lastPrice <= maxPrice) {
      const midX = priceToX(lastPrice);
      ctx.strokeStyle = COLORS.midLine;
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(midX, padding.top);
      ctx.lineTo(midX, chartHeight - padding.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      // Price label
      ctx.fillStyle = COLORS.midLine;
      ctx.beginPath();
      ctx.roundRect(midX - 35, padding.top - 18, 70, 18, 3);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px "JetBrains Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`$${formatPrice(lastPrice)}`, midX, padding.top - 5);
    }

    // Y-axis labels (volume)
    ctx.fillStyle = COLORS.text;
    ctx.font = '10px "JetBrains Mono", monospace';
    ctx.textAlign = 'left';

    for (let i = 0; i <= 4; i++) {
      const total = maxTotal * (1 - i / 4);
      const y = padding.top + (i / 4) * drawHeight;
      ctx.fillText(formatVolume(total), width - padding.right + 8, y + 4);
    }

    // X-axis labels (price)
    ctx.textAlign = 'center';
    for (let i = 0; i <= 6; i++) {
      const price = minPrice + (i / 6) * priceRange;
      const x = padding.left + (i / 6) * chartWidth;
      ctx.fillText(formatPrice(price), x, chartHeight - 10);
    }

    // Wall markers (large orders)
    const threshold = maxTotal * 0.15;
    
    [...bids, ...asks].forEach(({ price, total }, idx) => {
      if (total > threshold) {
        const x = priceToX(price);
        const y = totalToY(total);
        const isBid = idx < bids.length;
        
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = isBid ? COLORS.bidStroke : COLORS.askStroke;
        ctx.fill();
        ctx.strokeStyle = isBid ? 'rgba(0, 212, 170, 0.3)' : 'rgba(255, 107, 107, 0.3)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.stroke();
      }
    });

  }, [processedData, lastPrice, height, formatPrice, formatVolume]);

  // Mouse interaction
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const padding = { left: 10, right: 70 };
    const chartWidth = rect.width - padding.left - padding.right;
    
    const { minPrice, priceRange, bids, asks, maxTotal } = processedData;
    const price = minPrice + ((x - padding.left) / chartWidth) * priceRange;

    // Find closest point
    let closest: { price: number; total: number; side: 'bid' | 'ask' } | null = null;
    let minDist = Infinity;

    for (const bid of bids) {
      const dist = Math.abs(bid.price - price);
      if (dist < minDist && bid.price >= price) {
        minDist = dist;
        closest = { ...bid, side: 'bid' };
      }
    }

    for (const ask of asks) {
      const dist = Math.abs(ask.price - price);
      if (dist < minDist && ask.price <= price) {
        minDist = dist;
        closest = { ...ask, side: 'ask' };
      }
    }

    if (closest) {
      const priceToX = (p: number) => padding.left + ((p - minPrice) / priceRange) * chartWidth;
      const totalToY = (t: number) => 20 + (1 - t / maxTotal) * (height - 50);
      
      setHoveredData({
        ...closest,
        x: priceToX(closest.price),
        y: totalToY(closest.total),
      });
    } else {
      setHoveredData(null);
    }
  }, [processedData, height]);

  const handleMouseLeave = useCallback(() => {
    setHoveredData(null);
  }, []);

  // Calculate stats
  const stats = useMemo(() => {
    const bidTotal = processedData.bids.length > 0 
      ? processedData.bids[processedData.bids.length - 1].total 
      : 0;
    const askTotal = processedData.asks.length > 0 
      ? processedData.asks[processedData.asks.length - 1].total 
      : 0;
    const totalVolume = bidTotal + askTotal;
    const bidPercent = totalVolume > 0 ? (bidTotal / totalVolume) * 100 : 50;
    
    // Find walls
    const bidWall = [...processedData.bids].sort((a, b) => b.total - a.total)[0];
    const askWall = [...processedData.asks].sort((a, b) => b.total - a.total)[0];

    return {
      bidTotal,
      askTotal,
      bidPercent,
      askPercent: 100 - bidPercent,
      bidWall,
      askWall,
      imbalance: bidPercent > 55 ? 'BUY PRESSURE' : bidPercent < 45 ? 'SELL PRESSURE' : 'BALANCED',
    };
  }, [processedData]);

  return (
    <div className="flex flex-col" style={{ height }}>
      {/* Stats bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.06] bg-[#0d1117]">
        <div className="flex items-center gap-4 text-xs">
          <span className="text-[#8b949e]">Order Book Depth</span>
          <div className="flex items-center gap-2">
            <span className={cn(
              "px-2 py-0.5 rounded text-[10px] font-bold",
              stats.bidPercent > 55 ? "bg-[#00D4AA]/20 text-[#00D4AA]" : 
              stats.bidPercent < 45 ? "bg-[#FF6B6B]/20 text-[#FF6B6B]" : 
              "bg-[#3B82F6]/20 text-[#3B82F6]"
            )}>
              {stats.imbalance}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00D4AA]" />
            <span className="text-[#8b949e]">Bids</span>
            <span className="text-[#00D4AA] font-mono font-medium">{formatVolume(stats.bidTotal)}</span>
            <span className="text-[#8b949e]">({stats.bidPercent.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FF6B6B]" />
            <span className="text-[#8b949e]">Asks</span>
            <span className="text-[#FF6B6B] font-mono font-medium">{formatVolume(stats.askTotal)}</span>
            <span className="text-[#8b949e]">({stats.askPercent.toFixed(1)}%)</span>
          </div>
        </div>
      </div>

      {/* Bid/Ask ratio bar */}
      <div className="h-1 flex">
        <div 
          className="h-full bg-gradient-to-r from-[#00D4AA]/50 to-[#00D4AA]" 
          style={{ width: `${stats.bidPercent}%` }} 
        />
        <div 
          className="h-full bg-gradient-to-r from-[#FF6B6B] to-[#FF6B6B]/50" 
          style={{ width: `${stats.askPercent}%` }} 
        />
      </div>

      {/* Chart */}
      <div ref={containerRef} className="relative flex-1">
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />

        {/* Tooltip */}
        {hoveredData && (
          <div 
            className="absolute pointer-events-none bg-[#161b22] border border-white/[0.1] rounded-lg p-2 shadow-xl z-10"
            style={{ 
              left: Math.min(hoveredData.x + 10, (containerRef.current?.clientWidth || 0) - 140),
              top: Math.max(hoveredData.y - 30, 10),
            }}
          >
            <div className="text-xs space-y-1">
              <div className="flex items-center gap-2">
                <span className={cn(
                  "w-2 h-2 rounded-full",
                  hoveredData.side === 'bid' ? "bg-[#00D4AA]" : "bg-[#FF6B6B]"
                )} />
                <span className="text-[#8b949e] capitalize">{hoveredData.side}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[#8b949e]">Price:</span>
                <span className="text-white font-mono">${formatPrice(hoveredData.price)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[#8b949e]">Total:</span>
                <span className="text-white font-mono">{formatVolume(hoveredData.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Crosshair */}
        {hoveredData && (
          <>
            <div 
              className="absolute w-px h-full bg-white/20 pointer-events-none"
              style={{ left: hoveredData.x, top: 0 }}
            />
            <div 
              className="absolute w-full h-px bg-white/20 pointer-events-none"
              style={{ top: hoveredData.y, left: 0 }}
            />
          </>
        )}
      </div>
    </div>
  );
}
